/**
 * AUTH-14: Web-side pre-check for sign-up emails.
 *
 * Mirrors the API's server-side validation so we can surface the rejection
 * inline in the form before burning a Supabase magic-link send. The API is
 * still authoritative — this is UX only. The actual blocklist is fetched
 * from the Vite env var VITE_AUTH_BLOCKED_EMAIL_DOMAINS (comma-separated).
 *
 * Precedence:
 *   • VITE_AUTH_BLOCKED_EMAIL_DOMAINS set → use it verbatim
 *   • unset → fall back to DEFAULT_BLOCKED_DOMAINS from @qm/shared
 *
 * Enforcement gate:
 *   • VITE_BLOCK_TEST_EMAILS="true" → validate
 *   • otherwise → always accept (dev/staging noop)
 *
 * @param {string} email
 * @returns {string | null} rejection reason, or null if acceptable
 */
import {
  DEFAULT_BLOCKED_DOMAINS,
  parseBlockedDomains,
  validateSignupEmail as validateSignupEmailShared,
} from "@qm/shared/emailValidation";

function getBlockedDomains() {
  const envList = import.meta.env.VITE_AUTH_BLOCKED_EMAIL_DOMAINS;
  if (envList && typeof envList === "string" && envList.trim()) {
    return parseBlockedDomains(envList);
  }
  return new Set(DEFAULT_BLOCKED_DOMAINS);
}

export function validateSignupEmail(email) {
  // Mirror the API gate: only validate when explicitly enabled.
  const enabled = import.meta.env.VITE_BLOCK_TEST_EMAILS === "true";
  if (!enabled) return null;
  // Mirror the API alias behaviour: default to blocking aliases when the
  // blocklist is active. VITE_BLOCK_EMAIL_ALIASES="false" permits them.
  const blockAliases = import.meta.env.VITE_BLOCK_EMAIL_ALIASES !== "false";
  const result = validateSignupEmailShared(email, {
    blockedDomains: getBlockedDomains(),
    blockAliases,
  });
  return result.valid ? null : (result.reason ?? "Invalid email address");
}
